import { Link } from 'react-router-dom'
import { Mail, RefreshCw, Paperclip, AlertCircle, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { CenterSpinner } from '@/components/ui/Spinner'
import { EmptyState } from '@/components/ui/EmptyState'
import { Badge } from '@/components/ui/Badge'
import { useInbox } from '@/lib/api/hooks'
import { dateTime } from '@/lib/format'
import { api } from '@/lib/api/mockClient'
import { useToast } from '@/components/ui/ToastProvider'
import { useQueryClient } from '@tanstack/react-query'

export function InboxPage() {
  const { data, isLoading } = useInbox()
  const toast = useToast()
  const qc = useQueryClient()

  async function sync() {
    await api.triggerEmailSync()
    toast.show({ tone: 'info', title: 'Email sync elindítva' })
    qc.invalidateQueries({ queryKey: ['inbox'] })
  }

  return (
    <div>
      <PageHeader
        title="Beérkező"
        subtitle="A szamla@adaptivo.hu címre érkezett emailek és mellékletük állapota"
        actions={
          <Button onClick={sync} variant="secondary" leftIcon={<RefreshCw className="h-4 w-4" />}>
            Email sync
          </Button>
        }
      />

      {isLoading ? (
        <CenterSpinner />
      ) : !data || data.length === 0 ? (
        <EmptyState
          title="Üres a beérkező"
          description="Még nem érkezett feldolgozandó email."
          icon={<Mail className="h-8 w-8" />}
        />
      ) : (
        <Card padding="none">
          <ul className="divide-y divide-slate-100">
            {data.map((m) => (
              <li key={m.id} className="px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium text-slate-900 truncate max-w-[24rem]">{m.from}</span>
                      <span className="text-slate-400">·</span>
                      <span className="text-xs text-slate-500">{dateTime(m.receivedAt)}</span>
                    </div>
                    <div className="mt-0.5 text-sm text-slate-700 truncate max-w-[40rem]">{m.subject}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                      <Badge tone="neutral">
                        <Paperclip className="h-3 w-3" /> {m.attachmentsCount} melléklet
                      </Badge>
                      {m.looksLikeInvoice ? (
                        <Badge tone="ok">
                          <CheckCircle2 className="h-3 w-3" /> Számlának tűnik
                        </Badge>
                      ) : (
                        <Badge tone="warn">
                          <AlertCircle className="h-3 w-3" /> Nem számlának tűnik
                        </Badge>
                      )}
                      <span className="text-slate-500">
                        Státusz:{' '}
                        {m.processStatus === 'processed'
                          ? 'feldolgozva'
                          : m.processStatus === 'skipped'
                            ? 'kihagyva'
                            : m.processStatus === 'error'
                              ? 'hiba'
                              : 'új'}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 text-xs">
                    {m.linkedInvoiceIds.length > 0 && (
                      <Link
                        to={`/invoices/${m.linkedInvoiceIds[0]}`}
                        className="text-brand-600 hover:underline"
                      >
                        {m.linkedInvoiceIds.length} számla →
                      </Link>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  )
}
